import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Eye, EyeOff, FileText, Image as ImageIcon, Loader2, Upload, X } from 'lucide-react';
import { toast } from 'sonner';
import api, { getAttachmentsByEntity, uploadAttachment } from '../services/api';

/**
 * Thumbnail tile — image preview if available, otherwise a file icon with extension badge.
 */
function AttachmentTile({ att, isIncluded, onToggle }) {
  const [src, setSrc] = useState(null);
  const isImage = !!att.is_image;

  useEffect(() => {
    if (!isImage) return undefined;
    let url = null;
    api.get(`/api/attachments/${att.id}/thumbnail`, { responseType: 'blob' })
      .then(res => { url = URL.createObjectURL(res.data); setSrc(url); })
      .catch(() => setSrc(null));
    return () => { if (url) URL.revokeObjectURL(url); };
  }, [att.id, isImage]);

  const ext = (att.filename || '').split('.').pop()?.toUpperCase() || 'FILE';

  return (
    <button
      type="button"
      onClick={() => onToggle(att.id)}
      className={`relative group rounded-lg overflow-hidden border-2 transition-all ${
        isIncluded ? 'border-cyan-500 shadow-md' : 'border-slate-200 opacity-50'
      }`}
      data-testid={`attachment-toggle-${att.id}`}
      title={att.filename}
    >
      <div className="aspect-square bg-slate-100 flex items-center justify-center">
        {isImage ? (
          src ? (
            <img src={src} alt={att.filename} className="w-full h-full object-cover" />
          ) : (
            <ImageIcon size={24} className="text-slate-400" />
          )
        ) : (
          <div className="flex flex-col items-center justify-center gap-1 text-slate-500">
            <FileText size={26} />
            <span className="text-[10px] font-bold tracking-wide">{ext}</span>
          </div>
        )}
      </div>
      <div
        className={`absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center ${
          isIncluded ? 'bg-cyan-500 text-white' : 'bg-black/50 text-white'
        }`}
      >
        {isIncluded ? <Eye size={12} /> : <EyeOff size={12} />}
      </div>
      <div className="absolute bottom-0 left-0 right-0 bg-black/60 text-white text-[9px] px-1 py-0.5 truncate">
        {att.filename}
      </div>
    </button>
  );
}

/**
 * Email Attachment Gallery — used by Quote & Invoice email modals.
 *
 * Props:
 *  - sources:   array of { entity_type, entity_id } — places to load existing attachments from
 *  - uploadTo:  { entity_type, entity_id } — where newly-uploaded files are persisted
 *               (so they remain on the entity for future emails)
 *  - selectedIds: Set<string> — currently-included attachment ids
 *  - onSelectionChange: (Set<string>) => void
 *  - title:     header label (e.g. "Photos & Files")
 *  - emptyHint: text when no attachments are loaded yet
 */
export function EmailAttachmentGallery({
  sources = [],
  uploadTo,
  selectedIds,
  onSelectionChange,
  title = 'Photos & Files',
  emptyHint = 'No attachments yet.',
}) {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const sourcesKey = useMemo(
    () => sources.map(s => `${s.entity_type}:${s.entity_id}`).sort().join('|'),
    [sources]
  );

  // Load attachments from every source. De-dupe by id (an attachment could be referenced from
  // multiple sources but we only want to render it once).
  const reload = async () => {
    if (!sources.length) { setAttachments([]); return; }
    setLoading(true);
    try {
      const results = await Promise.all(
        sources.map(s =>
          getAttachmentsByEntity(s.entity_type, s.entity_id)
            .then(r => r.data || [])
            .catch(() => [])
        )
      );
      const merged = [];
      const seen = new Set();
      for (const arr of results) {
        for (const att of arr) {
          if (att?.id && !seen.has(att.id)) {
            seen.add(att.id);
            merged.push(att);
          }
        }
      }
      setAttachments(merged);
      // Pre-select all freshly-loaded attachments that aren't already in the selection
      const next = new Set(selectedIds);
      merged.forEach(a => next.add(a.id));
      // Avoid an unnecessary state update if nothing changed
      if (next.size !== selectedIds.size) onSelectionChange(next);
    } finally {
      setLoading(false);
    }
  };

  // Reload whenever the source list changes
  useEffect(() => {
    reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourcesKey]);

  const toggle = (id) => {
    const next = new Set(selectedIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onSelectionChange(next);
  };

  const toggleAll = () => {
    if (selectedIds.size === attachments.length && attachments.length > 0) {
      onSelectionChange(new Set());
    } else {
      onSelectionChange(new Set(attachments.map(a => a.id)));
    }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = ''; // allow re-uploading the same filename
    if (!files.length || !uploadTo?.entity_id) return;
    setUploading(true);
    let successCount = 0;
    const newIds = [];
    for (const file of files) {
      try {
        const form = new FormData();
        form.append('file', file);
        form.append('entity_type', uploadTo.entity_type);
        form.append('entity_id', uploadTo.entity_id);
        const res = await uploadAttachment(form);
        if (res.data?.id) {
          newIds.push(res.data.id);
          successCount++;
        }
      } catch (err) {
        toast.error(`Upload failed: ${file.name}`);
      }
    }
    setUploading(false);
    if (successCount) {
      toast.success(`${successCount} file${successCount > 1 ? 's' : ''} uploaded`);
      await reload();
      // Make sure the newly-uploaded ones are included
      const next = new Set(selectedIds);
      newIds.forEach(id => next.add(id));
      onSelectionChange(next);
    }
  };

  return (
    <div data-testid="email-attachment-gallery">
      <div className="flex items-center justify-between mb-2">
        <label className="text-xs font-medium flex items-center gap-1.5">
          <ImageIcon size={14} /> {title}
          {attachments.length > 0 && (
            <span className="text-slate-500 font-normal">
              ({selectedIds.size}/{attachments.length} selected)
            </span>
          )}
        </label>
        <div className="flex items-center gap-3">
          {attachments.length > 0 && (
            <button
              type="button"
              onClick={toggleAll}
              className="text-xs text-cyan-600 hover:text-cyan-800 font-medium"
              data-testid="toggle-all-attachments"
            >
              {selectedIds.size === attachments.length ? 'Deselect All' : 'Select All'}
            </button>
          )}
          {uploadTo?.entity_id && (
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={uploading}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs bg-cyan-500 text-white rounded hover:bg-cyan-600 disabled:opacity-50"
              data-testid="upload-more-attachments-btn"
            >
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {uploading ? 'Uploading…' : 'Upload More'}
            </button>
          )}
        </div>
      </div>
      <input
        ref={fileInputRef}
        type="file"
        multiple
        className="hidden"
        onChange={handleUpload}
        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt"
        data-testid="upload-more-file-input"
      />

      {loading ? (
        <div className="flex items-center justify-center py-6 text-slate-400 text-sm gap-2">
          <Loader2 size={14} className="animate-spin" /> Loading attachments…
        </div>
      ) : attachments.length === 0 ? (
        <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center text-xs text-slate-400">
          {emptyHint}
        </div>
      ) : (
        <div className="grid grid-cols-6 gap-2">
          {attachments.map(att => (
            <AttachmentTile
              key={att.id}
              att={att}
              isIncluded={selectedIds.has(att.id)}
              onToggle={toggle}
            />
          ))}
        </div>
      )}
    </div>
  );
}

export default EmailAttachmentGallery;
